import type { TennisMatchFormat } from '../../data/tennisMatchFormats';

/**
 * Pure tennis scoring engine (sc-337 / Tennis Phase 3).
 *
 * `reduce(format, points)` replays a point log into a score state. No React,
 * no Supabase, no side effects — the point log is the single source of
 * truth, score/stats/analytics are all projections of it. That gives exact
 * undo (drop the last point and re-reduce) and idempotent sync (re-applying
 * the same log always yields the same state) for free.
 */

export type Side = 'us' | 'them';

/** Which of a doubles side's two players is on serve. Singles never sets this. */
export type DoublesSlot = 'A' | 'B';

export interface MatchPoint {
  /** Which side won this point. This is the only input the engine needs to score a match. */
  winner: Side;
}

/** What winning the next point would clinch, most significant first: match > set > break (game on the opponent's serve) > game. */
export type PointPressure = 'match' | 'set' | 'break' | 'game';

const NO_PRESSURE: Record<Side, PointPressure | null> = { us: null, them: null };

export interface ReduceOptions {
  /** Doubles/mixed doubles rotate serve through 4 players instead of 2. */
  isDoubles?: boolean;
}

/** Raw point count within the current game (0,1,2,3,4+ — not yet translated to 0/15/30/40/Ad). */
export interface GamePointCount {
  ours: number;
  theirs: number;
}

/** Translate a game's raw point counts into the familiar display ladder. The engine owns this rule; a UI just calls it. */
export function formatGamePoint(count: GamePointCount, noAd: boolean): { ours: string; theirs: string } {
  const LADDER = ['0', '15', '30', '40'];
  const { ours, theirs } = count;
  if (ours < 3 && theirs < 3) {
    return { ours: LADDER[ours], theirs: LADDER[theirs] };
  }
  if (!noAd && ours >= 3 && theirs >= 3) {
    if (ours === theirs) return { ours: '40', theirs: '40' }; // "Deuce" — caller may render as one word when equal
    return ours > theirs ? { ours: 'AD', theirs: '40' } : { ours: '40', theirs: 'AD' };
  }
  // no-ad: once both reach 3+, it's a single deciding point at 40-40, otherwise normal ladder
  return {
    ours: ours >= 3 ? '40' : LADDER[ours],
    theirs: theirs >= 3 ? '40' : LADDER[theirs],
  };
}

export interface TiebreakScore {
  ours: number;
  theirs: number;
}

export interface SetScore {
  ourGames: number;
  theirGames: number;
  /** Present if this set (or its match-tiebreak substitute) ended in a tiebreak. */
  tiebreak?: TiebreakScore;
  /** True when this "set" is actually a deciding match tiebreak substituted for a full set (decidingSet: 'match_tiebreak'). */
  isMatchTiebreak?: boolean;
}

export interface MatchState {
  format: TennisMatchFormat;
  /** Completed sets, in order. */
  sets: SetScore[];
  /** In-progress set's game count. Null once the match is over. */
  currentSet: { ourGames: number; theirGames: number } | null;
  /** In-progress game. Null once the match is over (or between a completed tiebreak-set and the next set's first point). */
  game: {
    isTB: boolean;
    points: GamePointCount;
  } | null;
  /** Who serves the current (or next) point. */
  server: Side;
  /** Which of the server's side's two players, for doubles. Undefined for singles. */
  servingSlot?: DoublesSlot;
  isBreakPoint: boolean;
  isSetPoint: boolean;
  isMatchPoint: boolean;
  /** What each side would clinch by winning the next point (the most significant one), or null. Lets a UI badge the right side. */
  pressure: Record<Side, PointPressure | null>;
  /** True while playing the match's final (set-win-deciding) set. */
  isDeciding: boolean;
  /** True immediately after a point/game that should trigger an end change. */
  changeEnds: boolean;
  /** Null until the match is decided. */
  winner: Side | null;
}

const other = (side: Side): Side => (side === 'us' ? 'them' : 'us');

function initialState(format: TennisMatchFormat): MatchState {
  return {
    format,
    sets: [],
    currentSet: { ourGames: 0, theirGames: 0 },
    game: { isTB: false, points: { ours: 0, theirs: 0 } },
    server: 'us', // arbitrary, stable starting point — who actually serves first is a pre-match coin toss the engine doesn't model
    servingSlot: undefined,
    isBreakPoint: false,
    isSetPoint: false,
    isMatchPoint: false,
    pressure: NO_PRESSURE,
    isDeciding: format.sets === 1,
    changeEnds: false,
    winner: null,
  };
}

/** Standard in-set tiebreaks are always to 7, win by 2 — decidingTiebreakPoints only applies to a deciding-set match-tiebreak substitute. */
const STANDARD_TIEBREAK_POINTS = 7;

/** Exported for src/services/tennis/statsProjection.ts, which reuses this to detect break points from a point's game-local tally without re-deriving the win rule. */
export function isGameWon(points: GamePointCount, noAd: boolean): Side | null {
  const { ours, theirs } = points;
  if (noAd && ours >= 3 && theirs >= 3) {
    // Sudden death: whoever is ahead by 1 at 40-40+ wins immediately.
    if (ours > theirs) return 'us';
    if (theirs > ours) return 'them';
    return null;
  }
  if (ours >= 4 && ours - theirs >= 2) return 'us';
  if (theirs >= 4 && theirs - ours >= 2) return 'them';
  return null;
}

function isTiebreakWon(score: TiebreakScore, target: number): Side | null {
  if (score.ours >= target && score.ours - score.theirs >= 2) return 'us';
  if (score.theirs >= target && score.theirs - score.ours >= 2) return 'them';
  return null;
}

/** First to `gamesPerSet` games with a 2-game margin; reaching tiebreakAt-tiebreakAt instead starts a tiebreak (handled by the caller, not here). */
function isSetWonByGames(games: { ourGames: number; theirGames: number }, gamesPerSet: number): Side | null {
  const { ourGames, theirGames } = games;
  if (ourGames >= gamesPerSet && ourGames - theirGames >= 2) return 'us';
  if (theirGames >= gamesPerSet && theirGames - ourGames >= 2) return 'them';
  return null;
}

/** Fixed 2 (singles) or 4 (doubles) player serve rotation. Index 0 is whichever side/slot serves game 1. */
function rotationOrder(isDoubles: boolean): Array<{ side: Side; slot?: DoublesSlot }> {
  if (!isDoubles) return [{ side: 'us' }, { side: 'them' }];
  return [
    { side: 'us', slot: 'A' },
    { side: 'them', slot: 'A' },
    { side: 'us', slot: 'B' },
    { side: 'them', slot: 'B' },
  ];
}

/**
 * Server for regular game index `gameIndex` (0-based, counts only completed non-tiebreak games
 * within the current set — a set-ending tiebreak doesn't itself consume a "game" slot; the set
 * that follows continues the rotation from where this one left off).
 */
function serverForGame(gameIndex: number, isDoubles: boolean): { side: Side; slot?: DoublesSlot } {
  const order = rotationOrder(isDoubles);
  return order[gameIndex % order.length];
}

/** Tiebreak serving: first point continues the rotation; the server then changes after point 1, then every 2 points. */
function serverForTiebreakPoint(
  startIndex: number,
  pointNumber: number, // 1-based
  isDoubles: boolean
): { side: Side; slot?: DoublesSlot } {
  const order = rotationOrder(isDoubles);
  const serverSlotIndex = pointNumber === 1 ? 0 : 1 + Math.floor((pointNumber - 2) / 2);
  return order[(startIndex + serverSlotIndex) % order.length];
}

/** Would `side` winning the very next point end the current game/tiebreak? */
function isOnePointFromWinning(
  side: Side,
  game: MatchState['game'],
  format: TennisMatchFormat,
  tiebreakTarget: number
): boolean {
  if (!game) return false;
  if (game.isTB) {
    const hypothetical: TiebreakScore =
      side === 'us'
        ? { ours: game.points.ours + 1, theirs: game.points.theirs }
        : { ours: game.points.ours, theirs: game.points.theirs + 1 };
    return isTiebreakWon(hypothetical, tiebreakTarget) === side;
  }
  const hypothetical: GamePointCount =
    side === 'us'
      ? { ours: game.points.ours + 1, theirs: game.points.theirs }
      : { ours: game.points.ours, theirs: game.points.theirs + 1 };
  return isGameWon(hypothetical, format.noAd) === side;
}

/** Would `side` winning the current game/tiebreak also win the set they're playing? */
function wouldWinSet(
  side: Side,
  currentSet: { ourGames: number; theirGames: number },
  format: TennisMatchFormat,
  inTiebreak: boolean
): boolean {
  if (inTiebreak) return true; // winning a set-ending tiebreak always wins the set
  const hypothetical =
    side === 'us'
      ? { ourGames: currentSet.ourGames + 1, theirGames: currentSet.theirGames }
      : { ourGames: currentSet.ourGames, theirGames: currentSet.theirGames + 1 };
  return isSetWonByGames(hypothetical, format.gamesPerSet) === side;
}

/** Would `side` winning their current set also win the match? */
function wouldWinMatch(side: Side, sets: SetScore[], format: TennisMatchFormat): boolean {
  const setsWon = sets.filter((s) => (s.ourGames > s.theirGames ? 'us' : 'them') === side).length;
  return setsWon + 1 >= format.sets;
}

/**
 * Replay a full point log into a score state. Never throws on an incomplete
 * sequence — a match abandoned mid-set (rain, retirement, default) simply
 * returns whatever partial state the points played out to, with
 * `winner: null`.
 */
export function reduce(format: TennisMatchFormat, points: MatchPoint[], options: ReduceOptions = {}): MatchState {
  const isDoubles = options.isDoubles ?? false;
  let state = initialState(format);
  // How many regular (non-tiebreak) games have been served in the CURRENT set, for rotation purposes.
  let regularGamesInSet = 0;
  // Carried across the whole match so a mid-set tiebreak (or the set after one) continues the same rotation.
  let rotationIndex = 0;

  for (const point of points) {
    if (state.winner) break; // ignore any points appended after the match is already decided
    state = applyPoint(state, point, { regularGamesInSet, rotationIndex }, (next) => {
      regularGamesInSet = next.regularGamesInSet;
      rotationIndex = next.rotationIndex;
    });
  }

  return withServerAndFlags(state, isDoubles, regularGamesInSet, rotationIndex);
}

function applyPoint(
  state: MatchState,
  point: MatchPoint,
  rotation: { regularGamesInSet: number; rotationIndex: number },
  commitRotation: (next: { regularGamesInSet: number; rotationIndex: number }) => void
): MatchState {
  if (!state.game || !state.currentSet) return state; // defensive: shouldn't happen while winner is null

  const decidingSetSubstitute =
    state.isDeciding && state.format.sets > 1 && state.format.decidingSet === 'match_tiebreak';

  // ----- Deciding-set match-tiebreak substitute: the whole "set" is one breaker. -----
  if (decidingSetSubstitute) {
    const target = state.format.decidingTiebreakPoints ?? 10;
    const nextTB: TiebreakScore =
      point.winner === 'us'
        ? { ours: state.game.points.ours + 1, theirs: state.game.points.theirs }
        : { ours: state.game.points.ours, theirs: state.game.points.theirs + 1 };
    const tbWinner = isTiebreakWon(nextTB, target);
    const game = { isTB: true, points: nextTB };
    if (!tbWinner) {
      return { ...state, game };
    }
    const setResult: SetScore = {
      ourGames: tbWinner === 'us' ? 1 : 0,
      theirGames: tbWinner === 'us' ? 0 : 1,
      tiebreak: nextTB,
      isMatchTiebreak: true,
    };
    const sets = [...state.sets, setResult];
    return finishSetOrMatch(state, sets, rotation, commitRotation);
  }

  // ----- Normal game (not in a tiebreak). -----
  if (!state.game.isTB) {
    const nextPoints: GamePointCount =
      point.winner === 'us'
        ? { ours: state.game.points.ours + 1, theirs: state.game.points.theirs }
        : { ours: state.game.points.ours, theirs: state.game.points.theirs + 1 };
    const gameWinner = isGameWon(nextPoints, state.format.noAd);
    if (!gameWinner) {
      return { ...state, game: { isTB: false, points: nextPoints } };
    }
    return finishGame(state, gameWinner, rotation, commitRotation);
  }

  // ----- Tiebreak in progress (set-ending, not the deciding-set substitute). -----
  const nextTB: TiebreakScore =
    point.winner === 'us'
      ? { ours: state.game.points.ours + 1, theirs: state.game.points.theirs }
      : { ours: state.game.points.ours, theirs: state.game.points.theirs + 1 };
  const tbWinner = isTiebreakWon(nextTB, STANDARD_TIEBREAK_POINTS);
  if (!tbWinner) {
    return { ...state, game: { isTB: true, points: nextTB } };
  }
  const setResult: SetScore = {
    ourGames: tbWinner === 'us' ? state.currentSet.ourGames + 1 : state.currentSet.ourGames,
    theirGames: tbWinner === 'them' ? state.currentSet.theirGames + 1 : state.currentSet.theirGames,
    tiebreak: nextTB,
  };
  const sets = [...state.sets, setResult];
  return finishSetOrMatch(state, sets, rotation, commitRotation);
}

function finishGame(
  state: MatchState,
  gameWinner: Side,
  rotation: { regularGamesInSet: number; rotationIndex: number },
  commitRotation: (next: { regularGamesInSet: number; rotationIndex: number }) => void
): MatchState {
  if (!state.currentSet) return state;
  const nextSetGames = {
    ourGames: gameWinner === 'us' ? state.currentSet.ourGames + 1 : state.currentSet.ourGames,
    theirGames: gameWinner === 'them' ? state.currentSet.theirGames + 1 : state.currentSet.theirGames,
  };
  commitRotation({ regularGamesInSet: rotation.regularGamesInSet + 1, rotationIndex: rotation.rotationIndex + 1 });

  const setWinner = isSetWonByGames(nextSetGames, state.format.gamesPerSet);
  if (setWinner) {
    const sets = [...state.sets, { ourGames: nextSetGames.ourGames, theirGames: nextSetGames.theirGames }];
    return finishSetOrMatch(state, sets, rotation, commitRotation);
  }

  if (nextSetGames.ourGames === state.format.tiebreakAt && nextSetGames.theirGames === state.format.tiebreakAt) {
    return { ...state, currentSet: nextSetGames, game: { isTB: true, points: { ours: 0, theirs: 0 } } };
  }

  return { ...state, currentSet: nextSetGames, game: { isTB: false, points: { ours: 0, theirs: 0 } } };
}

function finishSetOrMatch(
  state: MatchState,
  sets: SetScore[],
  rotation: { regularGamesInSet: number; rotationIndex: number },
  commitRotation: (next: { regularGamesInSet: number; rotationIndex: number }) => void
): MatchState {
  const lastSet = sets[sets.length - 1];
  const setWinner: Side = lastSet.ourGames > lastSet.theirGames ? 'us' : 'them';
  const ourSetsWon = sets.filter((s) => s.ourGames > s.theirGames).length;
  const theirSetsWon = sets.length - ourSetsWon;

  commitRotation({ regularGamesInSet: 0, rotationIndex: rotation.rotationIndex });

  if (ourSetsWon >= state.format.sets || theirSetsWon >= state.format.sets) {
    return {
      ...state,
      sets,
      currentSet: null,
      game: null,
      winner: ourSetsWon >= state.format.sets ? 'us' : 'them',
    };
  }

  void setWinner;
  const nowDeciding = ourSetsWon === state.format.sets - 1 && theirSetsWon === state.format.sets - 1;
  // Entering a deciding set whose format substitutes a single match tiebreak: that breaker IS the
  // whole "set", so the next game starts straight in tiebreak mode rather than as a normal game.
  const nextSetIsMatchTiebreak = nowDeciding && state.format.sets > 1 && state.format.decidingSet === 'match_tiebreak';
  return {
    ...state,
    sets,
    currentSet: { ourGames: 0, theirGames: 0 },
    game: { isTB: nextSetIsMatchTiebreak, points: { ours: 0, theirs: 0 } },
    isDeciding: nowDeciding,
  };
}

function withServerAndFlags(
  state: MatchState,
  isDoubles: boolean,
  regularGamesInSet: number,
  rotationIndex: number
): MatchState {
  if (!state.game || !state.currentSet || state.winner) {
    return { ...state, server: state.server, servingSlot: state.servingSlot, isBreakPoint: false, isSetPoint: false, isMatchPoint: false, pressure: NO_PRESSURE, changeEnds: false };
  }

  const decidingSetSubstitute =
    state.isDeciding && state.format.sets > 1 && state.format.decidingSet === 'match_tiebreak';

  let server: Side;
  let servingSlot: DoublesSlot | undefined;
  let changeEnds = false;

  if (state.game.isTB) {
    const pointNumber = state.game.points.ours + state.game.points.theirs + 1;
    const startIndex = rotationIndex % rotationOrder(isDoubles).length;
    const served = serverForTiebreakPoint(startIndex, pointNumber, isDoubles);
    server = served.side;
    servingSlot = served.slot;
    const pointsPlayed = pointNumber - 1;
    changeEnds = pointsPlayed > 0 && pointsPlayed % 6 === 0;
  } else {
    const served = serverForGame(regularGamesInSet, isDoubles);
    server = served.side;
    servingSlot = served.slot;
    const gamesPlayed = state.currentSet.ourGames + state.currentSet.theirGames;
    changeEnds = gamesPlayed > 0 && gamesPlayed % 2 === 1 && state.game.points.ours === 0 && state.game.points.theirs === 0;
  }

  const tiebreakTarget = decidingSetSubstitute
    ? state.format.decidingTiebreakPoints ?? 10
    : STANDARD_TIEBREAK_POINTS;

  const usOneFromWin = isOnePointFromWinning('us', state.game, state.format, tiebreakTarget);
  const themOneFromWin = isOnePointFromWinning('them', state.game, state.format, tiebreakTarget);

  const usWouldWinSet = usOneFromWin && (decidingSetSubstitute || wouldWinSet('us', state.currentSet, state.format, state.game.isTB));
  const themWouldWinSet = themOneFromWin && (decidingSetSubstitute || wouldWinSet('them', state.currentSet, state.format, state.game.isTB));

  const usWouldWinMatch = usWouldWinSet && wouldWinMatch('us', state.sets, state.format);
  const themWouldWinMatch = themWouldWinSet && wouldWinMatch('them', state.sets, state.format);

  const returner = other(server);
  const isBreakPoint =
    !state.game.isTB && ((returner === 'us' && usOneFromWin) || (returner === 'them' && themOneFromWin));

  const pressureFor = (side: Side, oneFromWin: boolean, wouldWinSet: boolean, wouldWinMatch: boolean): PointPressure | null => {
    if (!oneFromWin) return null;
    if (wouldWinMatch) return 'match';
    if (wouldWinSet) return 'set';
    // A tiebreak point isn't a "break" — the tiebreak as a whole has no single server.
    if (state.game?.isTB) return null;
    return side === returner ? 'break' : 'game';
  };

  return {
    ...state,
    server,
    servingSlot,
    isBreakPoint,
    isSetPoint: usWouldWinSet || themWouldWinSet,
    isMatchPoint: usWouldWinMatch || themWouldWinMatch,
    pressure: {
      us: pressureFor('us', usOneFromWin, usWouldWinSet, usWouldWinMatch),
      them: pressureFor('them', themOneFromWin, themWouldWinSet, themWouldWinMatch),
    },
    changeEnds,
  };
}
